import React, { useState, useEffect, useRef } from 'react';
import { FaMale, FaFemale, FaGenderless } from 'react-icons/fa';
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  addDoc,
  setDoc,
  deleteDoc,
  doc,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  connectFirestoreEmulator
} from 'firebase/firestore';
import {
  getStorage,
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  connectStorageEmulator
} from 'firebase/storage';
import {db,storage} from "./firebase";


connectFirestoreEmulator(db, "localhost", 8080);
connectStorageEmulator(storage, "localhost", 9199);

// -------------------- Constants --------------------
const EMOJIS = ["😀", "😂", "😍", "😘", "😢", "😡", "👍", "🙏", "🔥", "💖", "🎉", "🌹", "🍕", "☕️", "🎵"];
const INTERESTS = ["Music", "Movies", "Sports", "Travel", "Food", "Gaming", "Books"];

// -------------------- Game Prompts --------------------
const GAME_PROMPTS = {
  low: ["You're adorable!", "I love your smile!", "Thinking of you 💖"],
  medium: ["You look amazing 😘", "Let's have some fun 🔥", "Can't wait to see you!"],
  high: ["You drive me wild 💋", "I can't stop thinking of you 😈", "Let's get closer 🔥"]
};

// -------------------- Main App --------------------
function App() {
  const [name, setName] = useState('');
  const [gender, setGender] = useState('female');
  const [roomCode, setRoomCode] = useState('');
  const [inputRoomCode, setInputRoomCode] = useState('');
  const [joined, setJoined] = useState(false);
  const [messages, setMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [message, setMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [selectedLevel, setSelectedLevel] = useState('low');
  const [chatMode, setChatMode] = useState('text'); // text/audio/video
  const [selectedInterests, setSelectedInterests] = useState([]);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  // -------------------- Scroll to bottom --------------------
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // -------------------- Real-time Firestore Updates --------------------
  useEffect(() => {
    if (!joined) return;
    const messagesRef = collection(db, 'rooms', roomCode, 'messages');
    const q = query(messagesRef, orderBy('createdAt'));
    const unsubscribeMessages = onSnapshot(q, snapshot => {
      setMessages(snapshot.docs.map(doc => doc.data()));
    });
    const usersRef = collection(db, 'rooms', roomCode, 'users');
    const unsubscribeUsers = onSnapshot(usersRef, snapshot => {
      setUsers(snapshot.docs.map(doc => doc.data()));
    });
    return () => {
      unsubscribeMessages();
      unsubscribeUsers();
    };
  }, [joined, roomCode]);

  // -------------------- Firestore Functions --------------------
  const addUserToRoom = async (room, username, gender) => {
    const userRef = doc(db, 'rooms', room, 'users', username);
    await setDoc(userRef, { name: username, gender, joinedAt: serverTimestamp(), interests: selectedInterests });
  };

  const removeUserFromRoom = async (room, username) => {
    const userRef = doc(db, 'rooms', room, 'users', username);
    await deleteDoc(userRef);
  };

  const joinRoom = async () => {
    if (!name.trim()) return alert('Enter your name');
    if (!inputRoomCode.trim()) return alert('Enter a room code');
    const code = inputRoomCode.trim();
    setRoomCode(code);
    setJoined(true);
    await addUserToRoom(code, name, gender);
  };

  const createRoom = async () => {
    if (!name.trim()) return alert('Enter your name');
    const code = Math.random().toString(36).substring(2, 8);
    setRoomCode(code);
    setInputRoomCode(code);
    setJoined(true);
    await addUserToRoom(code, name, gender);
  };

  const disconnect = async () => {
    await removeUserFromRoom(roomCode, name);
    setJoined(false);
    setRoomCode('');
    setInputRoomCode('');
    setMessages([]);
    setMessage('');
    setName('');
    setGender('female');
    setEmojiPickerOpen(false);
    setUsers([]);
    setSelectedInterests([]);
  };

  // -------------------- Message Functions --------------------
  const sendMessage = async (text) => {
    if (chatMode !== 'text') return; // Only send if text mode
    let msg = text ?? message;
    if (typeof msg !== 'string') return;
    msg = msg.trim();
    if (!msg) return;
    const messagesRef = collection(db, 'rooms', roomCode, 'messages');
    await addDoc(messagesRef, {
      text: msg,
      createdAt: serverTimestamp(),
      senderName: name,
      senderGender: gender,
      imageUrl: null,
      audioUrl: null,
      videoUrl: null
    });
    if (!text) setMessage('');
    setEmojiPickerOpen(false);
  };

  const sendMediaMessage = async (fileOrBlob, type) => {
    if (!fileOrBlob) return;
    setUploading(true);
    try {
      const folder = type === 'image' ? 'chatImages' : type === 'audio' ? 'chatAudio' : 'chatVideo';
      const extension = type === 'image' ? fileOrBlob.name?.split('.').pop() : 'webm';
      const mediaRef = storageRef(storage, `${folder}/${roomCode}/${Date.now()}.${extension}`);
      await uploadBytes(mediaRef, fileOrBlob);
      const url = await getDownloadURL(mediaRef);
      const messagesRef = collection(db, 'rooms', roomCode, 'messages');
      await addDoc(messagesRef, {
        text: '',
        createdAt: serverTimestamp(),
        senderName: name,
        senderGender: gender,
        imageUrl: type === 'image' ? url : null,
        audioUrl: type === 'audio' ? url : null,
        videoUrl: type === 'video' ? url : null
      });
    } catch (err) {
      console.error(err);
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} upload failed: ${err.message}`);
    }
    setUploading(false);
  };

  const onFileChange = (e) => {
    if (e.target.files.length > 0) sendMediaMessage(e.target.files[0], 'image');
    e.target.value = '';
  };

  // -------------------- Recording Functions --------------------
  const startRecording = async (type = 'audio') => {
    try {
      const constraints = type === 'audio' ? { audio: true } : { audio: true, video: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = e => chunks.push(e.data);
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: type === 'audio' ? 'audio/webm' : 'video/webm' });
        await sendMediaMessage(blob, type);
        stream.getTracks().forEach(track => track.stop());
        setMediaRecorder(null);
      };
      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
    } catch (err) {
      alert('Recording failed: ' + err.message);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder) {
      mediaRecorder.stop();
      setRecording(false);
    }
  };

  // -------------------- UI Helpers --------------------
const addEmoji = (emoji) => {
  setMessage(prev => prev + emoji);
  setEmojiPickerOpen(false); // 👈 close picker after selection
};
  const genderIcon = (g) =>
    g === 'male' ? <FaMale style={{ color: '#1976d2' }} /> :
      g === 'female' ? <FaFemale style={{ color: '#d81b60' }} /> :
        <FaGenderless style={{ color: '#6a1b9a' }} />;

  const sendGamePrompt = (level) => {
    const prompts = GAME_PROMPTS[level];
    const prompt = prompts[Math.floor(Math.random() * prompts.length)];
    setMessage(prompt);
  };

  // -------------------- Render --------------------
  if (!joined) {
    return (
      <div style={styles.container}>
        <h1 style={styles.title}>Couples Chat 💞</h1>
        <div style={styles.formGroup}>
          <label>Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} style={styles.input} />
        </div>
        <div style={styles.formGroup}>
          <label>Gender</label>
          <select value={gender} onChange={e => setGender(e.target.value)} style={styles.input}>
            <option value="female">Female</option>
            <option value="male">Male</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div style={styles.formGroup}>
          <label>Room Code</label>
          <input type="text" placeholder="Enter room code" value={inputRoomCode} onChange={e => setInputRoomCode(e.target.value)} style={styles.input} />
        </div>
        {/* Interests Selection */}
        <div style={styles.formGroup}>
          <label>Select Interests</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 4 }}>
            {INTERESTS.map(interest => (
              <button
                key={interest}
                type="button"
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: selectedInterests.includes(interest) ? '2px solid #6a1b9a' : '1px solid #ccc',
                  backgroundColor: selectedInterests.includes(interest) ? '#f48fb1' : '#fff',
                  cursor: 'pointer'
                }}
                onClick={() => {
                  setSelectedInterests(prev =>
                    prev.includes(interest) ? prev.filter(i => i !== interest) : [...prev, interest]
                  );
                }}
              >
                {interest}
              </button>
            ))}
            <input
              type="text"
              placeholder="Add custom interest"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  const customInterest = e.target.value.trim();
                  if (!selectedInterests.includes(customInterest)) {
                    setSelectedInterests([...selectedInterests, customInterest]);
                  }
                  e.target.value = '';
                }
              }}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #ccc',
                flexShrink: 0,
                minWidth: 120
              }}
            />
          </div>
        </div>
        <button onClick={joinRoom} style={{ ...styles.button, backgroundColor: '#6a1b9a' }}>Join Room</button>
        <button onClick={createRoom} style={{ ...styles.button, backgroundColor: '#2e7d32' }}>Create New Room</button>
      </div>
    );
  }

  return (
    <div style={styles.chatContainer}>
      {/* Header */}
      <div style={styles.header}>
        <h2>Room: {roomCode}</h2>
        <button onClick={disconnect} style={styles.disconnectBtn}>Disconnect</button>
      </div>

      {/* Users */}
      <div style={styles.usersList}>
        Online: {users.map(u => <span key={u.name} style={{ marginRight: 8 }}>{genderIcon(u.gender)} {u.name}</span>)}
      </div>

      {/* Messages */}
      <div style={styles.messages}>
        {messages.length === 0 && <p style={{ textAlign: 'center', color: '#888' }}>No messages yet</p>}
        {messages.map((msg, i) => {
          const isMe = msg.senderName === name;
          return (
            <div key={i} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
              <div style={{ ...styles.messageBubble, backgroundColor: isMe ? '#d1c4e9' : '#f8bbd0', borderRadius: isMe ? '20px 20px 0 20px' : '20px 20px 20px 0' }}>
                <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                  {genderIcon(msg.senderGender)} {msg.senderName}
                </div>
                {msg.text && <div style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</div>}
                {msg.imageUrl && <img src={msg.imageUrl} alt="shared" style={styles.image} onClick={() => window.open(msg.imageUrl, '_blank')} />}
                {msg.audioUrl && <audio controls src={msg.audioUrl} style={{ marginTop: 8 }} />}
                {msg.videoUrl && <video controls src={msg.videoUrl} style={{ marginTop: 8, maxWidth: '100%' }} />}
              </div>
            </div>
          );
        })}
        <div ref={messagesEndRef} />
      </div>

      {/* Controls Top */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
      

        {/* Game Buttons */}
        <button onClick={() => sendGamePrompt('low')} style={styles.gameBtn}>💌 Cute</button>
        <button onClick={() => sendGamePrompt('medium')} style={styles.gameBtn}>🔥 Flirty</button>
        <button onClick={() => sendGamePrompt('high')} style={styles.gameBtn}>💋 Seductive</button>

        {/* Media Mode Buttons */}
        <button onClick={() => setChatMode('text')} style={styles.gameBtn}>📝 Text</button>
        <button onClick={() => setChatMode('audio')} style={styles.gameBtn}>🎤 Audio</button>
        <button onClick={() => setChatMode('video')} style={styles.gameBtn}>🎥 Video</button>
      </div>

      {/* Input Area */}
      {chatMode === 'text' && (
        <div style={styles.inputContainer}>
          <input
            type="text"
            placeholder={uploading ? 'Uploading...' : 'Type a message...'}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && sendMessage()}
            style={styles.messageInput}
            disabled={uploading}
          />
            {/* Emoji */}
        <button type="button" onClick={() => setEmojiPickerOpen(!emojiPickerOpen)} style={styles.emojiBtn}>😊</button>
        {emojiPickerOpen && (
          <div style={styles.emojiPicker}>
            {EMOJIS.map((e, idx) => <span key={idx} style={styles.emoji} onClick={() => addEmoji(e)}>{e}</span>)}
          </div>
        )}

          <button onClick={() => fileInputRef.current.click()} style={styles.uploadBtn} disabled={uploading}>📷</button>
          <input type="file" accept="image/*" ref={fileInputRef} onChange={onFileChange} style={{ display: 'none' }} />
          <button onClick={() => sendMessage(message)} style={styles.sendBtn} disabled={uploading}>➤</button>
        </div>
      )}

      {chatMode === 'audio' && (
        <div style={styles.inputContainer}>
          <button onClick={recording ? stopRecording : () => startRecording('audio')} style={styles.uploadBtn} disabled={uploading}>
            {recording ? '⏹️ Stop' : '🎤 Record'}
          </button>
        </div>
      )}

      {chatMode === 'video' && (
        <div style={styles.inputContainer}>
          <button onClick={recording ? stopRecording : () => startRecording('video')} style={styles.uploadBtn} disabled={uploading}>
            {recording ? '⏹️ Stop' : '🎥 Record'}
          </button>
        </div>
      )}

    </div>
  );
}

// -------------------- Styles --------------------
const styles = {
  container: { maxWidth: 400, margin: '3rem auto', fontFamily: 'Segoe UI, sans-serif', padding: 24, background: '#fff', borderRadius: 12, boxShadow: '0 3px 15px rgba(0,0,0,0.1)' },
  title: { textAlign: 'center', color: '#4a148c', marginBottom: 24 },
  formGroup: { marginBottom: 16 },
  input: { width: '100%', padding: 10, borderRadius: 6, border: '1px solid #ccc', marginTop: 4, fontSize: 16 },
  button: { width: '100%', padding: 12, color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', marginBottom: 8, fontSize: 16 },
  chatContainer: { maxWidth: 800, margin: '2rem auto', fontFamily: 'Segoe UI, sans-serif', padding: 16, border: '1px solid #ccc', borderRadius: 12, background: '#fff' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  disconnectBtn: { backgroundColor: '#d32f2f', color: '#fff', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' },
  usersList: { marginBottom: 12, color: '#555' },
  messages: { minHeight: 300, maxHeight: 500, overflowY: 'auto', border: '1px solid #eee', padding: 12, borderRadius: 12, background: '#f9f9f9', marginBottom: 12 },
  messageBubble: { padding: 10, maxWidth: '60%' },
  image: { marginTop: 8, maxWidth: '100%', borderRadius: 8, cursor: 'pointer' },
  inputContainer: { display: 'flex', gap: 8, position: 'relative', alignItems: 'center' },
  messageInput: { flexGrow: 1, padding: 10, borderRadius: 6, border: '1px solid #ccc', fontSize: 16 },
  sendBtn: { padding: '0 16px', borderRadius: 6, backgroundColor: '#6a1b9a', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16 },
  uploadBtn: { padding: '0 12px', borderRadius: 6, backgroundColor: '#1976d2', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 16 },
  emojiBtn: { padding: '0 12px', borderRadius: 6, backgroundColor: '#fbc02d', border: 'none', cursor: 'pointer', fontSize: 20 },
  emojiPicker: {
    position: 'absolute',
    bottom: '50px', // pushes it above the emoji button
    right: '80px',  // adjust depending on layout
    background: '#fff',
    border: '1px solid #ccc',
    borderRadius: 8,
    padding: 8,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    boxShadow: '0 4px 10px rgba(0,0,0,0.1)',
    zIndex: 1000,
  },
    emoji: { cursor: 'pointer', fontSize: 20 },
  gameBtn: { padding: '6px 12px', borderRadius: 6, border: '1px solid #ccc', backgroundColor: '#fff', cursor: 'pointer' }
};

export default App;
